import _ from "lodash";
import RequestError from "./RequestError";
import amProxyUrl from "./AmProxyUrl";
import api from "@/api";

/**
 * @typedef RequestConfig
 * @prop {string} path
 * @prop {string} method
 * @prop {Object} [params]
 * @prop {any} [data]
 */

export default {
  createMediaflow,
  getMediaflow,
  getMediaflows,
  updateMediaflows,
  deleteMediaflow,
  nameChange,
  toggleMediaflow,
  createFlow,
  updateFlow,
  createFlowMedia,
  updateFlowMedia,
  startStream,
  toggleFlowMedia,
  deleteFlowMedia,
  stopStream,
  deleteFlow,
  switchScene,
  transition,
  muteFlowMedia,
  getAudioSync,
  setAudioSync,

  uploadFlowImage,
  deleteFlowImage,
  reorderFlowMedia,

  getAdSchedules,
  createAdSchedule,
  updateAdSchedule,
  deleteAdSchedule,
  toggleAdSchedule
};

async function deleteFlowImage(mediaflowId, flowId, imageId) {
  return makeRequest({
    path: `/mediaflows/${mediaflowId}/flow/${flowId}/images/${imageId}`,
    method: 'delete'
  });
}

function uploadFlowImage(mediaflowId, flowId, fdata) {
	return makeRequest({
		path: `/mediaflows/${mediaflowId}/flow/${flowId}/images`,
		method: 'post',
		data: fdata,
		headers: {
			'content-type': 'multipart/form-data'
		}
	});
}

function reorderFlowMedia(mediaflowId, flowId, oldIndex, newIndex) {
	return makeRequest({
		path: `/mediaflows/${mediaflowId}/flow/${flowId}/media/reorder`,
		method: 'put',
		data: {
			oldIndex,
			newIndex
		}
	});
}

async function deleteFlowMedia(mediaflowId, flowId, mediaId) {
  return makeRequest({
    path: `/mediaflows/${mediaflowId}/flow/${flowId}/media/${mediaId}`,
    method: 'delete'
  });
}

async function deleteFlow(mediaflowId, flowId) {
  return makeRequest({
    path: `/mediaflows/${mediaflowId}/flow/${flowId}`,
    method: 'delete'
  });
}

async function switchScene(mediaflowId, flowId) {
  return makeRequest({
    path: `/mediaflows/${mediaflowId}/flow/${flowId}/switch`,
    method: 'post'
  });
}

async function transition(mediaflowId, data) {
  return makeRequest({
    path: `/mediaflows/${mediaflowId}/transition`,
    method: 'post',
    data
  });
}

async function muteFlowMedia(mediaflowId, flowId, mediaId, muted) {
  return makeRequest({
    path: `/mediaflows/${mediaflowId}/flow/${flowId}/media/${mediaId}/mute`,
    method: 'post',
    data: { muted }
  });
}

async function getAudioSync(mediaflowId, flowId, mediaId) {
  return makeRequest({
    path: `/mediaflows/${mediaflowId}/flow/${flowId}/media/${mediaId}/audio-sync`,
    method: 'get'
  });
}

async function setAudioSync(mediaflowId, flowId, mediaId, offset) {
  return makeRequest({
    path: `/mediaflows/${mediaflowId}/flow/${flowId}/media/${mediaId}/audio-sync`,
    method: 'put',
    data: { offset }
  });
}

async function stopStream(mediaflowId) {
	return makeRequest({
		  path: `/mediaflows/${mediaflowId}/flow/stop-session`,
		  method: 'post'
	  });
  }

function toggleFlowMedia(mediaflowId, flowId, mediaId) {
	return makeRequest({
	  path: `/mediaflows/${mediaflowId}/flow/${flowId}/media/${mediaId}/toggle`,
	  method: 'post'
	});
  }

function startStream(mediaflowId) {
	return makeRequest({
		  path: `/mediaflows/${mediaflowId}/flow/start-session`,
		  method: 'post'
	  });
  }

function createFlowMedia(mediaflowId, flowId, data) {
	return makeRequest({
		  path: `/mediaflows/${mediaflowId}/flow/${flowId}/media`,
		  method: 'post',
		  data
	  });
  }

function updateFlowMedia(mediaflowId, flowId, mediaId, data) {
	return makeRequest({
		  path: `/mediaflows/${mediaflowId}/flow/${flowId}/media/${mediaId}`,
		  method: 'put',
		  data
	  });
  }

function createFlow(id, name) {
  return makeRequest({
		path: `/mediaflows/${id}/flow`,
		method: 'post',
		data: {
			name: name
		}
	});
}

function updateFlow(mediaflowId, flowId, name) {
  return makeRequest({
		path: `/mediaflows/${mediaflowId}/flow/${flowId}`,
		method: 'put',
		data: {
			name: name
		}
	});
}

function toggleMediaflow(id) {
  return makeRequest({
		path: `/mediaflows/${id}/toggle`,
		method: 'post',
	});
}

function createMediaflow(name, destination) {
	return makeRequest({
		path: '/mediaflows',
		method: 'post',
		data: {
			name: name,
			destination: destination
		}
	});
}

function getMediaflows() {
	return makeRequest({
		path: '/mediaflows',
		method: 'get',
	});
}

function getMediaflow(id) {
  return makeRequest({
		path: `/mediaflows/${id}`,
		method: 'get',
	});
}

function updateMediaflows(id, data) {
  return makeRequest({
		path: `/mediaflows/${id}`,
		method: 'put',
    data: data
	});
}

function deleteMediaflow(id) {
  return makeRequest({
		path: `/mediaflows/${id}`,
		method: 'delete',
	});
}

function nameChange(playerId, name) {
  return makeRequest({
		path: `/mediaflows/${playerId}`,
		method: 'put',
        data: {
			name: name
		}
	});
}

function getAdSchedules(mediaflowId) {
  return makeRequest({
    path: `/mediaflows/${mediaflowId}/ad-schedules`,
    method: 'get'
  });
}

function createAdSchedule(mediaflowId, data) {
  return makeRequest({
    path: `/mediaflows/${mediaflowId}/ad-schedules`,
    method: 'post',
    data
  });
}

function updateAdSchedule(mediaflowId, adScheduleId, data) {
  return makeRequest({
    path: `/mediaflows/${mediaflowId}/ad-schedules/${adScheduleId}`,
    method: 'put',
    data
  });
}

function deleteAdSchedule(mediaflowId, adScheduleId) {
  return makeRequest({
    path: `/mediaflows/${mediaflowId}/ad-schedules/${adScheduleId}`,
    method: 'delete'
  });
}

function toggleAdSchedule(mediaflowId, adScheduleId) {
  return makeRequest({
    path: `/mediaflows/${mediaflowId}/ad-schedules/${adScheduleId}/toggle`,
    method: 'post'
  });
}

async function makeRequest(reqConfig, preventProxy, throwReal) {
  if (typeof reqConfig === "string") {
    reqConfig = {
      path: reqConfig,
    };
  }

  if (preventProxy !== true) {
    reqConfig = amProxyUrl(reqConfig, process.env.VUE_APP_VOD_API_BASE_URL);
  }

  reqConfig.url = reqConfig.path;
  reqConfig.timeout = 60000;

  let res;
  try {
    res = await api.request(reqConfig);
  } catch (err) {
    if (throwReal) {
      throw err;
    }
    const edata = _.get(err, "response.data");
    throw new RequestError(edata);
  }

  return res && res.data;
} 